import {
  currencySchema,
  donationBoxActionSchema,
  donationBoxStatusSchema,
  scopeLevelSchema,
} from '@btsis/core';
import { oc } from '@orpc/contract';
import { z } from 'zod';
import { commonErrors } from './common/errors.js';
import { bulkResultSchema, bulkRowSchema, sortedListRequest } from './common/list.js';
import { moneySchema } from './common/money.js';
import { paginated } from './common/pagination.js';
import { phoneSchema } from './common/phone.js';
import { queryBooleanSchema } from './common/query.js';

const guarded = {
  UNAUTHENTICATED: commonErrors.UNAUTHENTICATED,
  PERMISSION_DENIED: commonErrors.PERMISSION_DENIED,
  TWO_FACTOR_REQUIRED: commonErrors.TWO_FACTOR_REQUIRED,
  OUT_OF_SCOPE: commonErrors.OUT_OF_SCOPE,
  NOT_FOUND: commonErrors.NOT_FOUND,
  CONFLICT: commonErrors.CONFLICT,
  VALIDATION_FAILED: commonErrors.VALIDATION_FAILED,
};

/**
 * A collection becomes an ordinary donation, so confirming one can be refused
 * by every rule ordinary entry is refused by: the period's own gates, the three
 * `donated_at` gates and pricing.
 */
const collected = {
  ...guarded,
  DONATION_DATE_OUTSIDE_PERIOD: commonErrors.DONATION_DATE_OUTSIDE_PERIOD,
  DONATION_DATE_IN_FUTURE: commonErrors.DONATION_DATE_IN_FUTURE,
  DONATION_BACKDATE_NOT_PERMITTED: commonErrors.DONATION_BACKDATE_NOT_PERMITTED,
  PRICING_VIOLATION: commonErrors.PRICING_VIOLATION,
};

const timestampSchema = z.iso.datetime({ offset: true });

const boxCodeSchema = z.string().trim().min(1).max(60);
const keeperNameSchema = z.string().trim().min(2).max(200);
const keeperPhoneSchema = phoneSchema;
const addressSchema = z.string().trim().min(1).max(500);
const noteSchema = z.string().trim().max(2000);
const minorAmountSchema = z
  .string()
  .regex(/^\d{1,18}$/, 'must be a positive integer in minor units');

export const donationBoxSchema = z.object({
  id: z.uuid(),
  code: z.string(),
  status: donationBoxStatusSchema,
  keeperName: z.string().nullable(),
  /** As it was typed, never rewritten. */
  keeperPhone: z.string().nullable(),
  /** The same number in E.164; null when the typed value could not be read (docs/decisions/0086). */
  keeperPhoneE164: z.string().nullable(),
  placementAddress: z.string().nullable(),
  placedAt: z.string().nullable(),
  isActive: z.boolean(),
  institutionId: z.uuid(),
  institutionName: z.string(),
  countryId: z.uuid(),
  countryName: z.string(),
  zoneId: z.uuid(),
  zoneName: z.string(),
  regionId: z.uuid(),
  regionName: z.string(),
  /**
   * Summed from the collection rows on every read. The box keeps no running
   * total, because a stored one is a number that can be wrong — and a box
   * collecting in two currencies has two of them, never one converted figure.
   */
  collectedTotals: z.array(moneySchema),
  collectionCount: z.number().int(),
  lastCollectedAt: z.string().nullable(),
  /** Null for a box that is not out: nothing is outstanding to count days on. */
  daysSinceLastCollection: z.number().int().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type DonationBox = z.infer<typeof donationBoxSchema>;

export const donationBoxCollectionSchema = z.object({
  id: z.uuid(),
  donationBoxId: z.uuid(),
  collectedAt: z.string(),
  amount: moneySchema,
  note: z.string().nullable(),
  countedByUserId: z.uuid(),
  countedByUserName: z.string().nullable(),
  /** Null while the emptying is recorded and not yet confirmed into a donation. */
  donationId: z.uuid().nullable(),
  donationReferenceCode: z.string().nullable(),
  donationStatus: z.string().nullable(),
  createdAt: z.string(),
});

export type DonationBoxCollection = z.infer<typeof donationBoxCollectionSchema>;

export const donationBoxTransitionSchema = z.object({
  action: donationBoxActionSchema,
  to: donationBoxStatusSchema,
});

export type DonationBoxTransition = z.infer<typeof donationBoxTransitionSchema>;

/**
 * Which campaign period a box collection lands in, per scope. Configuration,
 * never code: a branch running its own box campaign is a row here.
 */
export const donationBoxSettingSchema = z.object({
  id: z.uuid(),
  scopeLevel: scopeLevelSchema,
  scopeId: z.uuid().nullable(),
  scopeName: z.string().nullable(),
  campaignPeriodId: z.uuid(),
  campaignId: z.uuid(),
  campaignName: z.string(),
  seasonYear: z.number().int().nullable(),
});

export type DonationBoxSetting = z.infer<typeof donationBoxSettingSchema>;

/**
 * One box a bulk retirement reached. A box outside the caller's scope reads as
 * missing, exactly as a single read of it does, so `OUT_OF_SCOPE` is not a
 * value this route can produce; the whole route is guarded by
 * `donationBox.edit`, so neither is `PERMISSION_DENIED`.
 */
export const donationBoxBulkResultSchema = bulkRowSchema([
  'NOT_FOUND',
  'DONATION_BOX_INVALID_TRANSITION',
]);

export type DonationBoxBulkResult = z.infer<typeof donationBoxBulkResultSchema>;

/** The most boxes one `donationBox.bulkRetire` call retires. */
export const DONATION_BOX_BULK_LIMIT = 500;

export const DONATION_BOX_SORT_FIELDS = [
  'code',
  'status',
  'placedAt',
  'createdAt',
  'institutionName',
] as const;

export type DonationBoxSortField = (typeof DONATION_BOX_SORT_FIELDS)[number];

export const donationBoxContract = {
  list: oc
    .route({
      method: 'GET',
      path: '/donation-boxes',
      summary: 'Donation boxes inside the caller’s scope',
    })
    .errors(guarded)
    .input(
      sortedListRequest(DONATION_BOX_SORT_FIELDS, 'code', 'asc').extend({
        status: donationBoxStatusSchema.optional(),
        institutionId: z.uuid().optional(),
        includeInactive: queryBooleanSchema.optional(),
        /**
         * The boxes-out report: placed boxes whose last emptying is older than
         * this many days, which is the operational question the legacy shape
         * could not answer at all.
         */
        uncollectedForDays: z.coerce.number().int().min(0).max(3650).optional(),
      }),
    )
    .output(paginated(donationBoxSchema)),

  get: oc
    .route({ method: 'GET', path: '/donation-boxes/{id}' })
    .errors(guarded)
    .input(z.object({ id: z.uuid() }))
    .output(
      z.object({
        box: donationBoxSchema,
        collections: z.array(donationBoxCollectionSchema),
        transitions: z.array(donationBoxTransitionSchema),
      }),
    ),

  create: oc
    .route({ method: 'POST', path: '/donation-boxes' })
    .errors(guarded)
    .input(
      z.object({
        code: boxCodeSchema,
        institutionId: z.uuid().optional(),
        keeperName: keeperNameSchema.nullish(),
        keeperPhone: keeperPhoneSchema.nullish(),
        placementAddress: addressSchema.nullish(),
      }),
    )
    .output(z.object({ box: donationBoxSchema })),

  /**
   * `expectedUpdatedAt` is the `updatedAt` the dialog was filled from. Sent,
   * and the box changed since, the edit is refused 409
   * `DONATION_BOX_CHANGED_SINCE_READ` rather than written over what somebody
   * else did; absent, nothing is compared.
   */
  update: oc
    .route({ method: 'PATCH', path: '/donation-boxes/{id}' })
    .errors(guarded)
    .input(
      z.object({
        id: z.uuid(),
        code: boxCodeSchema.optional(),
        keeperName: keeperNameSchema.nullish(),
        keeperPhone: keeperPhoneSchema.nullish(),
        placementAddress: addressSchema.nullish(),
        isActive: z.boolean().optional(),
        expectedUpdatedAt: timestampSchema.optional(),
      }),
    )
    .output(z.object({ box: donationBoxSchema })),

  /**
   * `in_stock → placed` and `collected → placed`: a box cycles. The keeper is
   * named here rather than on the box record, because a placement is when the
   * organization learns who is holding it.
   */
  place: oc
    .route({ method: 'POST', path: '/donation-boxes/{id}/placement' })
    .errors(guarded)
    .input(
      z.object({
        id: z.uuid(),
        keeperName: keeperNameSchema,
        keeperPhone: keeperPhoneSchema.nullish(),
        placementAddress: addressSchema.nullish(),
        placedAt: timestampSchema.optional(),
      }),
    )
    .output(z.object({ box: donationBoxSchema })),

  retire: oc
    .route({ method: 'POST', path: '/donation-boxes/{id}/retirement' })
    .errors(guarded)
    .input(z.object({ id: z.uuid() }))
    .output(z.object({ box: donationBoxSchema })),

  /**
   * Retiring the whole selection, one transaction and one audit row per box —
   * a bulk endpoint is never one transaction, so a box the machine refuses does
   * not roll back the boxes before it
   * (docs/decisions/0026-donation-approval.md, amended 2026-09-15).
   */
  bulkRetire: oc
    .route({ method: 'POST', path: '/donation-boxes/retirements' })
    .errors(guarded)
    .input(z.object({ ids: z.array(z.uuid()).min(1).max(DONATION_BOX_BULK_LIMIT) }))
    .output(bulkResultSchema(donationBoxBulkResultSchema)),

  /**
   * The emptying, and the donation it becomes. `confirm` decides whether the
   * collection is recorded alone or whether the donation is written in the same
   * transaction: an operator who has counted the cash but not yet checked it
   * against the bank records the count and confirms later.
   *
   * The donation carries `entry_channel = 'donation_box'`, no donor at all —
   * `donations_donor_or_box` is what lets it — and the collection's id, and it
   * goes through the same pricing, the same freeze and the same totals writer
   * as every other donation. Nothing downstream knows a box was involved.
   */
  collect: oc
    .route({ method: 'POST', path: '/donation-boxes/{id}/collections' })
    .errors(collected)
    .input(
      z.object({
        id: z.uuid(),
        amount: z.object({ amountMinor: minorAmountSchema, currency: currencySchema }),
        collectedAt: timestampSchema.optional(),
        note: noteSchema.nullish(),
        confirm: z.boolean().default(true),
      }),
    )
    .output(
      z.object({
        box: donationBoxSchema,
        collection: donationBoxCollectionSchema,
      }),
    ),

  /** A collection recorded but not confirmed becomes its donation here. */
  confirmCollection: oc
    .route({ method: 'POST', path: '/donation-boxes/{id}/collections/{collectionId}/confirmation' })
    .errors(collected)
    .input(z.object({ id: z.uuid(), collectionId: z.uuid() }))
    .output(z.object({ box: donationBoxSchema, collection: donationBoxCollectionSchema })),

  /** Where a box collection lands, per scope. Read behind `donationBox.list`. */
  settingList: oc
    .route({ method: 'GET', path: '/donation-box-settings' })
    .errors(guarded)
    .input(z.object({}))
    .output(z.object({ settings: z.array(donationBoxSettingSchema) })),

  settingSet: oc
    .route({ method: 'PUT', path: '/donation-box-settings' })
    .errors(guarded)
    .input(
      z.object({
        scopeLevel: scopeLevelSchema,
        scopeId: z.uuid().nullable(),
        campaignPeriodId: z.uuid(),
      }),
    )
    .output(z.object({ setting: donationBoxSettingSchema })),

  settingClear: oc
    .route({ method: 'DELETE', path: '/donation-box-settings/{id}' })
    .errors(guarded)
    .input(z.object({ id: z.uuid() }))
    .output(z.object({ id: z.uuid() })),
};
